import * as api from '@/redux/api/conversationFolders';
import { getAuthToken, getBidderId } from '@/redux/modules/user';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from '@reduxjs/toolkit';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '../rootReducer';
import { LOG_OUT_BIDDER } from './actions';
import ms from 'ms';

/* Action Types */
export const ARCHIVE_CONVERSATION_FOLDER_FAIL = 'ARCHIVE_CONVERSATION_FOLDER_FAIL';
export const ARCHIVE_CONVERSATION_FOLDER_REQUEST = 'ARCHIVE_CONVERSATION_FOLDER_REQUEST';
export const ARCHIVE_CONVERSATION_FOLDER_SUCCESS = 'ARCHIVE_CONVERSATION_FOLDER_SUCCESS';

export const LOAD_CONVERSATION_FOLDERS_FAIL = 'LOAD_CONVERSATION_FOLDERS_FAIL';
export const LOAD_CONVERSATION_FOLDERS_REQUEST = 'LOAD_CONVERSATION_FOLDERS_REQUEST';
export const LOAD_CONVERSATION_FOLDERS_SUCCESS = 'LOAD_CONVERSATION_FOLDERS_SUCCESS';

export const CREATE_CONVERSATION_FOLDER_FAIL = 'CREATE_CONVERSATION_FOLDER_FAIL';
export const CREATE_CONVERSATION_FOLDER_REQUEST = 'CREATE_CONVERSATION_FOLDER_REQUEST';
export const CREATE_CONVERSATION_FOLDER_SUCCESS = 'CREATE_CONVERSATION_FOLDER_SUCCESS';

export const RENAME_CONVERSATION_FOLDER_FAIL = 'RENAME_CONVERSATION_FOLDER_FAIL';
export const RENAME_CONVERSATION_FOLDER_REQUEST = 'RENAME_CONVERSATION_FOLDER_REQUEST';
export const RENAME_CONVERSATION_FOLDER_SUCCESS = 'RENAME_CONVERSATION_FOLDER_SUCCESS';

const REDUX_STORE_TIME = ms('0.1s');

/* reducer */
export const DEFAULT_STATE = {
    folders: { systemFolders: [], userFolders: [] },
    loaded: 0,
    loading: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;

    switch (action.type) {
        case ARCHIVE_CONVERSATION_FOLDER_SUCCESS:
            existing = cloneDeep(state.folders);
            return {
                ...state,
                folders: {
                    ...existing,
                    userFolders: [...existing.userFolders].filter((f) => f.id !== action.meta.folderId),
                },
            };
        case CREATE_CONVERSATION_FOLDER_SUCCESS:
            existing = cloneDeep(state.folders);
            const userFolders = [...existing.userFolders, action.payload];
            userFolders.sort((a, b) => {
                const textA = a.name.toUpperCase();
                const textB = b.name.toUpperCase();
                return textA < textB ? -1 : textA > textB ? 1 : 0;
            });
            return {
                ...state,
                folders: {
                    ...existing,
                    userFolders,
                },
            };
        case LOAD_CONVERSATION_FOLDERS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_CONVERSATION_FOLDERS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_CONVERSATION_FOLDERS_SUCCESS:
            return {
                ...state,
                folders: action.payload,
                loaded: action.meta.actionTime,
                loading: false,
            };
        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.conversationFolders;

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getConversationFolders = createSelector(
    stateSelector,
    (state) => state.folders || { systemFolders: [], userFolders: [] }
);

export const getUnreadInboxCount = createSelector(getConversationFolders, (state) => {
    const firstTotal = state?.systemFolders?.reduce((total, folder) => total + folder.unReadConversationCount, 0) || 0;
    const secondTotal = state?.userFolders?.reduce((total, folder) => total + folder.unReadConversationCount, 0) || 0;
    return firstTotal + secondTotal;
});

const shouldFetch = createSelector([loadedSelector, loadingSelector], (loaded, loading) => {
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) {
        return false;
    }
    return !loading;
});

const loadConversationFolders = () => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATION_FOLDERS_REQUEST,
        });
        const response = await api.fetchConversationFolders({ authToken, bidderId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_CONVERSATION_FOLDERS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CONVERSATION_FOLDERS_FAIL,
        });
    }
};

export const fetchConversationFoldersIfNeeded = () => async (dispatch: Function, getState: Function) => {
    if (shouldFetch(getState())) {
        return dispatch(loadConversationFolders());
    }
    return Promise.resolve();
};

export const createConversationFolder = (name: string) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: CREATE_CONVERSATION_FOLDER_REQUEST,
        });
        const response = await api.postNewConversationFolders({ authToken, bidderId, deployment, name });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: CREATE_CONVERSATION_FOLDER_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: CREATE_CONVERSATION_FOLDER_FAIL,
        });
    }
};

export const renameConversationFolder =
    (folderId: number, name: string) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);
            dispatch({
                type: RENAME_CONVERSATION_FOLDER_REQUEST,
            });
            const response = await api.patchRenameConversationFolders({
                authToken,
                bidderId,
                deployment,
                folderId,
                name,
            });

            dispatch(fetchConversationFoldersIfNeeded());
            return dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: RENAME_CONVERSATION_FOLDER_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                payload: error,
                type: RENAME_CONVERSATION_FOLDER_FAIL,
            });
        }
    };

export const archiveConversationFolder = (folderId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: ARCHIVE_CONVERSATION_FOLDER_REQUEST,
        });
        const response = await api.deleteArchiveConversationFolders({ authToken, bidderId, deployment, folderId });
        return dispatch({
            meta: { actionTime: Date.now(), folderId },
            payload: response.payload,
            type: ARCHIVE_CONVERSATION_FOLDER_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: ARCHIVE_CONVERSATION_FOLDER_FAIL,
        });
    }
};
